import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { convertToCamelCase } from '@mgm/common/utils';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getDashboardData } from '../../../store/dashboard/selectors';
import { getCurrencyByCode } from '../../../store/common/selectors';

export function useDashboard() {
  const { t } = useTranslation(['common', 'dashboard']);

  const { transactions, sheets } = useAppSelector(getDashboardData);
  const getCurrency = useAppSelector(getCurrencyByCode);

  const formattedTransactions = { ...transactions };
  const formattedSheets = { ...sheets };

  const getTransactionsItems = useMemo(() => [
    {
      title: t(`${convertToCamelCase(formattedTransactions.incomeWidget?.title)}`, { ns: 'dashboard' }),
      tooltip: t(`${convertToCamelCase(formattedTransactions.incomeWidget?.tooltip)}`, { ns: 'dashboard' }),
      value: `${formattedTransactions.incomeWidget?.value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${getCurrency().mark}`,
      id: 'incomeWidget',
      chartData: formattedTransactions.incomeWidget?.chartData,
      color: '#a75a57',
      formattedValue: parseInt(formattedTransactions.incomeWidget?.value?.toString(), 10),
    },
    {
      title: t(`${convertToCamelCase(formattedTransactions.refundsWidget?.title)}`, { ns: 'dashboard' }),
      tooltip: t(`${convertToCamelCase(formattedTransactions.refundsWidget?.tooltip)}`, { ns: 'dashboard' }),
      value: `${formattedTransactions.refundsWidget?.value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${getCurrency().mark}`,
      id: 'refundsWidget',
      chartData: formattedTransactions.refundsWidget?.chartData,
      color: '#FFBD98',
      formattedValue: parseInt(formattedTransactions.refundsWidget?.value?.toString(), 10),
    },
    {
      title: t(`${convertToCamelCase(formattedTransactions.subtotalWidget?.title)?.replace(/'/g, '')}`, { ns: 'dashboard' }),
      tooltip: t(`${convertToCamelCase(formattedTransactions.subtotalWidget?.tooltip)}`, { ns: 'dashboard' }),
      chartData: formattedTransactions.subtotalWidget?.chartData,
      value: `${formattedTransactions.subtotalWidget?.value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${getCurrency().mark}`,
      id: 'subtotalWidget',
      color: '#93ea7e',
      formattedValue: parseInt(formattedTransactions.subtotalWidget?.value?.toString(), 10),
    },
  ], [formattedTransactions]);

  const getTimesheetsItems = useMemo(() => {
    const {
      hoursCostWidget, hoursWorkedWidget, shiftsWidget,
    } = formattedSheets ?? {};

    return [
      {
        title: t(`${convertToCamelCase(hoursCostWidget?.title)}`),
        value: `${hoursCostWidget?.value?.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${getCurrency().mark}`,
        tooltip: t(`${convertToCamelCase(hoursCostWidget?.tooltip)}`, { ns: 'dashboard' }),
        color: '#9E86FF',
        id: 'hoursCost',
        formattedValue: parseInt(hoursCostWidget?.value?.toString(), 10),
      },
      {
        title: t(`${convertToCamelCase(hoursWorkedWidget?.title)}`),
        value: hoursWorkedWidget?.value,
        id: 'hoursWorked',
        tooltip: t(`${convertToCamelCase(hoursWorkedWidget?.tooltip)}`, { ns: 'dashboard' }),
        color: '#83CFFF',
        formattedValue: parseInt(hoursWorkedWidget?.value?.toString(), 10),
      },
      {
        title: t(`${convertToCamelCase(shiftsWidget?.title)}`),
        value: shiftsWidget?.value,
        id: 'shiftsWidget',
        tooltip: t(`${convertToCamelCase(shiftsWidget?.tooltip)}`, { ns: 'dashboard' }),
        color: '#60F1C8',
        formattedValue: shiftsWidget?.value,
      },
    ];
  }, [formattedSheets]);

  return {
    getTransactionsItems,
    getTimesheetsItems,
  };
}
